import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';

import type { PurchasesResponse } from './endpoints/PurchasesEndpoint.types';
import type { PurchaseDetail } from './endpoints/PurchaseEndpoint.types';
import type { PurchaseSuppliersResponse } from './endpoints/PurchaseSuppliersEndpoint.types';
import type { CreatePurchaseBody, CreatePurchaseResponse } from './endpoints/CreatePurchaseEndpoint.types';
import type { UpdatePurchaseBody, UpdatePurchaseResponse } from './endpoints/UpdatePurchaseEndpoint.types';
import type { DeletePurchaseResponse, PurchaseMutationSuccess } from './endpoints/PurchaseDocumentEndpoint.types';

export const PURCHASE_SWR_KEY = 'purchase';
export const PURCHASE_SUPPLIERS_SWR_KEY = 'purchase-suppliers';

export interface PurchasesFilters {
  search?: string;
  totalExclVat?: number;
  totalInclVat?: number;
}

export async function getPurchases(
  cursor?: string | null,
  take?: number,
  filters?: PurchasesFilters,
  sortBy?: string,
  sortOrder?: string,
): Promise<PurchasesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.totalExclVat != null) params.set('totalExclVat', filters.totalExclVat.toString());
  if (filters?.totalInclVat != null) params.set('totalInclVat', filters.totalInclVat.toString());
  if (sortBy) params.set('sortBy', sortBy);
  if (sortOrder) params.set('sortOrder', sortOrder);

  const queryString = params.toString();
  const url = queryString ? `/api/v1/purchases?${queryString}` : '/api/v1/purchases';
  return fetchEndpointData<PurchasesResponse>(url);
}

export async function getPurchase(id: string): Promise<PurchaseDetail> {
  return fetchEndpointData<PurchaseDetail>(`/api/v1/purchases/${id}`);
}

export interface PurchaseSuppliersFilters {
  search?: string;
  hasOpenOrders?: boolean;
}

export async function getPurchaseSuppliers(
  cursor?: number | null,
  take?: number,
  filters?: PurchaseSuppliersFilters,
): Promise<PurchaseSuppliersResponse> {
  const params = new URLSearchParams();
  if (cursor != null) params.set('id', cursor.toString());
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.hasOpenOrders != null) params.set('hasOpenOrders', filters.hasOpenOrders ? 'true' : 'false');

  const queryString = params.toString();
  const url = queryString ? `/api/v1/purchases/suppliers?${queryString}` : '/api/v1/purchases/suppliers';
  return fetchEndpointData<PurchaseSuppliersResponse>(url);
}

export async function createPurchase(body: CreatePurchaseBody): Promise<CreatePurchaseResponse> {
  return fetchEndpointData<CreatePurchaseResponse>('/api/v1/purchases', {
    method: 'POST',
    body,
  });
}

export async function updatePurchase(id: string, body: UpdatePurchaseBody): Promise<UpdatePurchaseResponse> {
  return fetchEndpointData<UpdatePurchaseResponse>(`/api/v1/purchases/${id}`, {
    method: 'PATCH',
    body,
  });
}

export async function deletePurchase(id: string): Promise<DeletePurchaseResponse> {
  return fetchEndpointData<DeletePurchaseResponse>(`/api/v1/purchases/${id}`, {
    method: 'DELETE',
  });
}

export async function linkPurchaseDocument(purchaseId: string, documentId: string): Promise<PurchaseMutationSuccess> {
  return fetchEndpointData<PurchaseMutationSuccess>(`/api/v1/purchases/${purchaseId}/documents`, {
    method: 'POST',
    body: { documentId },
  });
}

export async function unlinkPurchaseDocument(purchaseId: string, documentId: string): Promise<PurchaseMutationSuccess> {
  return fetchEndpointData<PurchaseMutationSuccess>(`/api/v1/purchases/${purchaseId}/documents/${documentId}`, {
    method: 'DELETE',
  });
}

export function usePurchase(id: string) {
  return useSWR([PURCHASE_SWR_KEY, id], () => getPurchase(id));
}

export function usePurchaseSuppliers(filters: PurchaseSuppliersFilters, take = 100) {
  return useSWR([PURCHASE_SUPPLIERS_SWR_KEY, take, filters.search, filters.hasOpenOrders], () =>
    getPurchaseSuppliers(undefined, take, filters),
  );
}
